/**
 * "Clear ended free months for paying users" — the panel's decisions, pure (2026-09-27).
 *
 * Backend: routers/admin_free_month_backfill.py + services/free_month_backfill.py
 * (migration 1834). A run is a PREVIEW (how many closed free months there are, how many
 * qualify, why the rest do not, their 8-character ids; nothing is written) or an APPLY,
 * allowed once per finished preview from the last hour, which RE-READS the closed free
 * months and converts every user who qualifies now (the preview's ids are not replayed).
 * At most one run at a time. The server enforces every rule below; the panel mirrors
 * them so its buttons say why they are off. Tests: __tests__/freeMonthBackfillLogic.test.ts.
 */

export type BackfillMode = 'preview' | 'apply';
export type BackfillStatus = 'running' | 'done' | 'failed';

export interface BackfillSummary {
  /** 'scanning' while the closed free months are read; 'converting' while an apply converts. */
  phase?: 'scanning' | 'converting';
  closed_months?: number;
  scanned?: number;
  targets?: number;
  skipped?: Record<string, number>;
  converted?: number;
  stamped?: number;
  stamp_failed?: number;
  /** The first 8 characters of each qualifying user id — never an email or a name. */
  target_ids8?: string[];
}

export interface BackfillRun {
  id: string;
  mode: BackfillMode;
  status: BackfillStatus;
  preview_run_id: string | null;
  started_by: string;
  started_at: string;
  heartbeat_at: string;
  finished_at: string | null;
  processed: number;
  total: number | null;
  summary: BackfillSummary;
  error: string | null;
}

/** services/free_month_backfill.PREVIEW_MAX_AGE */
export const PREVIEW_MAX_AGE_MS = 60 * 60 * 1000;
/** services/free_month_backfill.RUN_STALE_AFTER — a "running" run whose heartbeat is
 *  older is gone (its server task was replaced); the server closes it on the next read. */
export const RUN_STALE_AFTER_MS = 10 * 60 * 1000;

/** Whether the poll should keep going: while a run is in progress — and, when a read of the
 *  runs FAILED (``latest`` null), while the last good read had one, so a transient error
 *  never freezes the spinner on a run that is still going. */
export function keepPolling(latest: BackfillRun[] | null, previous: BackfillRun[], now: Date = new Date()): boolean {
  return runningRun(latest ?? previous, now) !== null;
}

/** The confirm dialog: restates exactly what the apply writes. */
export function applyConfirmText(preview: BackfillRun): string {
  return 'Re-read the closed free months and convert every user who qualifies now? '
    + 'For each one this marks the ended free month converted_to_paid and re-labels the account in RevenueCat. '
    + `The preview counted ${qualifying(preview)}; the apply checks everyone again, so the number can differ. `
    + 'A repeat converts nobody twice.';
}

/** Why a closed free month was not converted, in the owner's words. */
export const SKIP_NAMES: Readonly<Record<string, string>> = {
  not_paying_now: 'Not paying now',
  no_charge_since_close: 'No real charge since the free month ended (a promotional grant or a charge from before does not count)',
  stopped_paying: 'Stopped paying before the conversion',
  access_unreadable: 'Access could not be read at the conversion — never converted',
  user_missing: 'The account no longer exists',
  history_unreadable: 'Payment history could not be read — never converted',
};

export function skipName(reason: string): string {
  return SKIP_NAMES[reason] ?? reason;
}

/** The run in progress — never one whose heartbeat went stale (a run a deploy cancelled
 *  must not keep Preview and Apply off and the panel polling forever). */
export function runningRun(runs: BackfillRun[], now: Date = new Date()): BackfillRun | null {
  return runs.find((r) => r.status === 'running'
    && now.getTime() - new Date(r.heartbeat_at).getTime() <= RUN_STALE_AFTER_MS) ?? null;
}

/** The newest preview, whatever its status (runs arrive newest first). */
export function latestPreview(runs: BackfillRun[]): BackfillRun | null {
  return runs.find((r) => r.mode === 'preview') ?? null;
}

export interface ApplyTarget {
  preview: BackfillRun | null;
  /** Why Apply is off; null when it is on. */
  reason: string | null;
}

/** Which preview Apply would follow, or why it cannot. */
export function applyTarget(runs: BackfillRun[], now: Date = new Date()): ApplyTarget {
  if (runningRun(runs, now)) return { preview: null, reason: 'A run is in progress.' };
  const preview = latestPreview(runs);
  if (!preview || preview.status !== 'done' || !preview.finished_at) {
    return { preview: null, reason: 'Run a preview first.' };
  }
  if (now.getTime() - new Date(preview.finished_at).getTime() > PREVIEW_MAX_AGE_MS) {
    return { preview: null, reason: 'The last preview is more than an hour old — run a new one.' };
  }
  const applied = runs.some((r) => r.mode === 'apply' && r.preview_run_id === preview.id
    && (r.status === 'running' || r.status === 'done'));
  if (applied) return { preview: null, reason: 'The last preview has been applied — run a new one to apply again.' };
  if ((preview.summary?.targets ?? 0) === 0) {
    return { preview: null, reason: 'The last preview found nobody to convert.' };
  }
  return { preview, reason: null };
}

export function qualifying(preview: BackfillRun): number {
  return preview.summary?.targets ?? 0;
}

export function skipRows(run: BackfillRun): Array<{ reason: string; name: string; users: number }> {
  return Object.entries(run.summary?.skipped ?? {})
    .map(([reason, users]) => ({ reason, name: skipName(reason), users }))
    .sort((a, b) => b.users - a.users || a.reason.localeCompare(b.reason));
}

/** One line of progress or outcome. */
export function runLine(run: BackfillRun): string {
  const total = run.total ?? 0;
  const s = run.summary ?? {};
  if (run.status === 'running') {
    if (run.total === null) return 'Starting…';
    // processed / total count the CURRENT phase's list (the server writes both per phase),
    // so the scan never reads as "37 of 3".
    return s.phase === 'converting'
      ? `Converting ${run.processed} of ${total} qualifying users… ${s.converted ?? 0} converted`
      : `Checking ${run.processed} of ${total} closed free months… ${s.targets ?? 0} qualify so far`;
  }
  if (run.status === 'failed') {
    // processed counts the users HANDLED in the converting phase (some are skipped), so
    // the line says how many were converted separately (wave-8 follow-ups 3). How far it
    // got is said only when the run knew its size — total is null until the first
    // progress write, so "after 0 of 0" would be a lie (wave-8 follow-ups 4, as the cohort
    // panel).
    const what = s.phase === 'converting'
      ? `qualifying users processed (${s.converted ?? 0} converted)`
      : 'closed free months checked';
    const where = run.total === null ? '' : ` after ${run.processed} of ${total} ${what}`;
    const again = ' Run a new preview — a repeat converts only who still qualifies.';
    if (run.error === 'slow_progress') {
      // Swept while its task was alive: it went quiet for longer than the stale window.
      return `Stopped: it took longer than 10 minutes to report progress, so it was closed${where}.${again}`;
    }
    if (run.error === 'stalled' || run.error === 'stopped') {
      return `Stopped: the server running it was replaced${where}.${again}`;
    }
    if (run.error === 'flag_off') {
      return 'Stopped: converting ended free months is switched off (FF_PURCHASE_CLEARS_CLOSED_FREE_MONTH).';
    }
    return run.total === null
      ? `Failed (${run.error ?? 'error'}).`
      : `Failed (${run.error ?? 'error'}) after ${run.processed} of ${total}.`;
  }
  return run.mode === 'preview'
    ? `Preview: ${s.closed_months ?? total} closed free months, ${s.targets ?? 0} would be converted. Nothing was written.`
    : `Applied: ${s.converted ?? 0} converted, ${s.stamped ?? 0} re-stamped in RevenueCat, `
      + `${s.stamp_failed ?? 0} stamp failed.`;
}
